export function formatDateToISO(date: Date): string {
	const year = date.getFullYear()
	const month = String(date.getMonth() + 1).padStart(2, "0")
	const day = String(date.getDate()).padStart(2, "0")
	return `${year}-${month}-${day}`
}

export function parseISODate(dateStr: string): Date {
	const [year, month, day] = dateStr.split("-").map(Number)
	return new Date(year, month - 1, day)
}

export function isValidDateString(dateStr: string): boolean {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false
	const date = parseISODate(dateStr)
	// Date rolls invalid day/month combos into a neighboring date (e.g. April 31 -> May 1) instead of failing, so round-trip and compare
	return !isNaN(date.getTime()) && formatDateToISO(date) === dateStr
}

// combines segmented year/month/day input (e.g. from SingleDatePicker) into a Date, returning
// undefined while a segment is still incomplete and null for an invalid calendar date
export function combineDateSegments(year: string, month: string, day: string): Date | null | undefined {
	if (year.length < 4 || month.length < 2 || day.length < 2) return undefined

	const iso = `${year}-${month}-${day}`
	return isValidDateString(iso) ? parseISODate(iso) : null
}

export function isExpired(expiryDate?: Date): boolean {
	const now = new Date()
	return expiryDate ? expiryDate < now : false
}

// Mirrors FivetranClient.SYNC_MAX_DURATION_IN_MINUTES in the API (api/src/adapters/fivetranClient.ts)
const SYNC_MAX_DURATION_MINUTES = 5

export function syncMinutesRemaining(launchDateMs: number, nowMs: number): number {
	const diffMinutes = Math.floor((nowMs - launchDateMs) / 1000 / 60)
	return Math.max(0, SYNC_MAX_DURATION_MINUTES - diffMinutes)
}

type RelativeTimeParts =
	| { key: "just-now" | "yesterday" | "absolute" }
	| { key: "minutes-ago" | "hours-ago" | "days-ago" | "weeks-ago"; count: number }

export function getRelativeTimeParts(date: Date, now: Date = new Date()): RelativeTimeParts {
	const diffMinutes = Math.floor((now.getTime() - date.getTime()) / 60000)
	if (diffMinutes < 1) return { key: "just-now" }
	if (diffMinutes < 60) return { key: "minutes-ago", count: diffMinutes }

	const diffHours = Math.floor(diffMinutes / 60)
	if (diffHours < 24) return { key: "hours-ago", count: diffHours }

	const diffDays = Math.floor(diffHours / 24)
	if (diffDays === 1) return { key: "yesterday" }
	if (diffDays < 7) return { key: "days-ago", count: diffDays }

	const diffWeeks = Math.floor(diffDays / 7)
	if (diffWeeks < 5) return { key: "weeks-ago", count: diffWeeks }

	return { key: "absolute" }
}

type TranslationValues = Record<string, number | string>
type Translate = (key: string, values: TranslationValues) => string

export function startOfDay(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function localeCode(locale: string): string {
	if (locale.startsWith("fr")) {
		return "fr-FR"
	}

	return "en-CA"
}

function formatTime(date: Date, locale: string): string {
	return new Intl.DateTimeFormat(localeCode(locale), {
		hour: "2-digit",
		minute: "2-digit",
		hourCycle: "h23",
	}).format(date)
}

export function startOfWeek(date: Date): Date {
	const day = date.getDay()
	const daysSinceMonday = day === 0 ? 6 : day - 1
	const weekStart = startOfDay(date)
	weekStart.setDate(weekStart.getDate() - daysSinceMonday)

	return weekStart
}

function englishOrdinal(day: number, locale: string, translate: Translate): string {
	if (!locale.startsWith("en")) {
		return ""
	}

	const category = new Intl.PluralRules(localeCode(locale), { type: "ordinal" }).select(day)

	return translate(`relative-date-time.ordinal.${category}`, {})
}

export function formatRelativeDateTime(timestamp: Date, now: Date, locale: string, translate: Translate): string {
	const timestampDay = startOfDay(timestamp)
	const today = startOfDay(now)

	if (timestampDay.getTime() === today.getTime()) {
		const elapsedMinutes = Math.max(0, Math.floor((now.getTime() - timestamp.getTime()) / 60_000))

		if (elapsedMinutes < 60) {
			return translate("relative-date-time.minutesAgo", { count: elapsedMinutes })
		}

		return translate("relative-date-time.hoursAgo", { count: Math.floor(elapsedMinutes / 60) })
	}

	const yesterday = new Date(today)
	yesterday.setDate(yesterday.getDate() - 1)
	const time = formatTime(timestamp, locale)

	if (timestampDay.getTime() === yesterday.getTime()) {
		return translate("relative-date-time.yesterday", { time })
	}

	if (timestampDay >= startOfWeek(now)) {
		const weekday = new Intl.DateTimeFormat(localeCode(locale), { weekday: "short" }).format(timestamp)

		return translate("relative-date-time.sameWeek", { time, weekday })
	}

	const month = new Intl.DateTimeFormat(localeCode(locale), { month: "short" }).format(timestamp)

	return translate("relative-date-time.beforeCurrentWeek", {
		day: timestamp.getDate(),
		month,
		ordinal: englishOrdinal(timestamp.getDate(), locale, translate),
		time,
	})
}
